import { useMemo, useState } from 'react'
import { Modal, Space, Progress } from 'antd'
import { BudgetBuilderModalProps } from './types'
import { TOTAL_STEPS } from './constants'
import { useBudgetBuilderState } from './hooks/useBudgetBuilderState'
import { IntroStep } from './components/steps/IntroStep'
import { CompanyStep } from './components/steps/CompanyStep'
import { SeasonStep } from './components/steps/SeasonStep'
import { OwnerStep } from './components/steps/OwnerStep'
import { FieldEmployeesStep } from './components/steps/FieldEmployeesStep'
import { AdminEmployeesStep } from './components/steps/AdminEmployeesStep'
import { TrucksStep } from './components/steps/TrucksStep'
import { TrailersStep } from './components/steps/TrailersStep'
import { HeavyMachineryStep } from './components/steps/HeavyMachineryStep'
import { BudgetBuilderFooter } from './components/BudgetBuilderFooter'
import { ExitConfirmationModal } from './components/ExitConfirmationModal'
import CrossInputCard from './components/CrossInputCard'
import { FieldWarning } from './inputSanityModel'
import { adminStepWarning, buildStepTotals, crewStepWarning, equipmentStepWarning } from './crossInputModel'
import { calculateWorkingWeeks } from './budgetBuilderModel'
import CrewCapacityCard from './components/CrewCapacityCard'
import { checkRevenueScale, evaluateCrewCapacity } from './crewCapacityModel'

const getStepName = (step: number): string => {
  const stepNames: Record<number, string> = {
    0: 'intro',
    1: 'company',
    2: 'season',
    3: 'owner',
    4: 'field-employees',
    5: 'admin-employees',
    6: 'trucks',
    7: 'trailers',
    8: 'heavy-machinery'
  }
  return stepNames[step] || 'unknown'
}

export const BudgetBuilderModal = ({ open, onCancel, createWorksheet }: BudgetBuilderModalProps) => {
  const [showExitConfirmation, setShowExitConfirmation] = useState(false)
  const state = useBudgetBuilderState(createWorksheet, onCancel)

  const handleCancel = () => {
    if (state.currentStep > 0) {
      setShowExitConfirmation(true)
    } else {
      onCancel()
    }
  }

  const handleConfirmExit = () => {
    window.posthog.capture('budget-builder-exited-early', {
      currentStep: state.currentStep,
      stepName: getStepName(state.currentStep)
    })
    const newWorksheet = state.createWorksheetFromBuilder()
    createWorksheet(newWorksheet, false)
    setShowExitConfirmation(false)
    onCancel()
  }

  const handleCancelExit = () => {
    setShowExitConfirmation(false)
  }

  const handleGetStarted = () => {
    window.posthog.capture('budget-builder-started', {
      timestamp: new Date().toISOString()
    })
    state.handleNext()
  }

  const progressPercent = state.currentStep === 0 ? 0 : ((state.currentStep / TOTAL_STEPS) * 100)

  const workingWeeks = state.seasonData.seasonStartDate !== null && state.seasonData.seasonEndDate !== null
    ? calculateWorkingWeeks(state.seasonData.seasonStartDate, state.seasonData.seasonEndDate)
    : 0

  const totals = buildStepTotals({
    companyData: state.companyData,
    ownerData: state.ownerData,
    fieldEmployeesData: state.fieldEmployeesData,
    adminEmployeesData: state.adminEmployeesData,
    trucksData: state.trucksData,
    trailersData: state.trailersData,
    heavyMachineryData: state.heavyMachineryData,
    workingWeeks
  })

  const snapshot = {
    companyData: state.companyData,
    seasonData: state.seasonData,
    ownerData: state.ownerData,
    fieldEmployeesData: state.fieldEmployeesData,
    adminEmployeesData: state.adminEmployeesData,
    trucksData: state.trucksData,
    trailersData: state.trailersData,
    heavyMachineryData: state.heavyMachineryData
  }

  const revenueScale = useMemo(
    () => checkRevenueScale(snapshot),
    [
      state.companyData,
      state.seasonData,
      state.ownerData,
      state.fieldEmployeesData,
      state.adminEmployeesData,
      state.trucksData,
      state.trailersData,
      state.heavyMachineryData
    ]
  )

  const crossInputCard = (warning: FieldWarning | null) => (
    <CrossInputCard
      warning={revenueScale ?? warning}
      salesGoal={state.companyData.salesGoal}
      onSalesGoalChange={(salesGoal) => state.updateCompanyData('salesGoal', salesGoal)}
    />
  )

  const capacity = useMemo(
    () => (state.currentStep === 4 ? evaluateCrewCapacity(snapshot) : null),
    [
      state.currentStep,
      state.companyData,
      state.seasonData,
      state.ownerData,
      state.fieldEmployeesData,
      state.adminEmployeesData,
      state.trucksData,
      state.trailersData,
      state.heavyMachineryData
    ]
  )

  const capacityCard = capacity === null ? null : (
    <CrewCapacityCard
      key={`${capacity.goal}-${capacity.crewCount}`}
      capacity={capacity}
      snapshot={snapshot}
      onApplyCrew={(crewCount, hoursPerWeek) => {
        if (hoursPerWeek !== null && hoursPerWeek > 0) {
          state.setCrewHoursPerWeek(hoursPerWeek)
        }
        state.setFieldEmployeeCount(crewCount)
      }}
      onApplyGoal={(salesGoal) => state.updateCompanyData('salesGoal', salesGoal)}
      onApplySplit={(ownShare, subcontracted) => {
        state.updateCompanyData('salesGoal', ownShare)
        state.updateCompanyData('subcontractedAmount', subcontracted)
      }}
    />
  )

  const renderStep = () => {
    switch (state.currentStep) {
      case 0:
        return <IntroStep onStart={handleGetStarted} />
      case 1:
        return <CompanyStep data={state.companyData} onChange={state.updateCompanyData} />
      case 2:
        return <SeasonStep data={state.seasonData} onChange={state.updateSeasonData} />
      case 3:
        return <OwnerStep data={state.ownerData} onChange={state.updateOwnerData} />
      case 4:
        return (
          <FieldEmployeesStep
            crossInputCard={crossInputCard(crewStepWarning(totals))}
            capacityCard={capacityCard}
            data={state.fieldEmployeesData}
            onCountChange={state.setFieldEmployeeCount}
            onEmployeeUpdate={state.updateFieldEmployee}
            onEmployeeAdd={state.addFieldEmployee}
            onEmployeeRemove={state.removeFieldEmployee}
          />
        )
      case 5:
        return (
          <AdminEmployeesStep
            crossInputCard={crossInputCard(adminStepWarning(totals))}
            data={state.adminEmployeesData}
            onCountChange={state.setAdminEmployeeCount}
            onEmployeeUpdate={state.updateAdminEmployee}
            onEmployeeAdd={state.addAdminEmployee}
            onEmployeeRemove={state.removeAdminEmployee}
          />
        )
      case 6:
        return (
          <TrucksStep
            crossInputCard={crossInputCard(equipmentStepWarning(totals))}
            data={state.trucksData}
            onCountChange={state.setTruckCount}
            onTruckUpdate={state.updateTruck}
            onTruckAdd={state.addTruck}
            onTruckRemove={state.removeTruck}
          />
        )
      case 7:
        return (
          <TrailersStep
            crossInputCard={crossInputCard(equipmentStepWarning(totals))}
            data={state.trailersData}
            onCountChange={state.setTrailerCount}
            onTrailerUpdate={state.updateTrailer}
            onTrailerAdd={state.addTrailer}
            onTrailerRemove={state.removeTrailer}
          />
        )
      case 8:
        return (
          <HeavyMachineryStep
            crossInputCard={crossInputCard(equipmentStepWarning(totals))}
            data={state.heavyMachineryData}
            onCountChange={state.setHeavyMachineryCount}
            onMachineryUpdate={state.updateHeavyMachinery}
            onMachineryAdd={state.addHeavyMachinery}
            onMachineryRemove={state.removeHeavyMachinery}
          />
        )
      default:
        return null
    }
  }

  return (
    <>
      <Modal
        title={
          <Space direction="vertical" size={8} style={{ width: '100%' }}>
            <span>Create New Budget</span>
            {state.currentStep > 0 && (
              <Progress
                percent={progressPercent}
                strokeColor="#ff6b4a"
                showInfo={false}
                style={{ margin: 0 }}
              />
            )}
          </Space>
        }
        open={open}
        onCancel={handleCancel}
        footer={null}
        width="100vw"
        style={{ top: 0, maxWidth: '100vw', paddingBottom: 0, margin: 0 }}
        styles={{
          body: { height: 'calc(100vh - 55px)', display: 'flex', flexDirection: 'column', padding: 0 },
          content: { borderRadius: 0 }
        }}
      >
        <div style={{
          flex: 1,
          overflow: 'auto',
          minHeight: 0
        }}>
          <div style={{
            minHeight: '100%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '20px 0'
          }}>
            {renderStep()}
          </div>
        </div>

        <BudgetBuilderFooter
          currentStep={state.currentStep}
          onBack={state.handleBack}
          onNext={state.handleNext}
          isNextDisabled={!state.isStepValid(state.currentStep)}
        />
      </Modal>

      <ExitConfirmationModal
        open={showExitConfirmation}
        onCancel={handleCancelExit}
        onConfirm={handleConfirmExit}
      />
    </>
  )
}

export default BudgetBuilderModal
